import { type ReactNode, useRef, useState } from 'react';

import { Box, Drawer, DrawerProps, Stack, Typography } from '@mui/material';

import ActionButton from '~components/buttons/actionButton';
import ActionButtonStyle from '~components/buttons/actionButtonStyle';
import { FormContainerContext } from '~contexts';
import { StatusVariant } from '~enums';
import { FormWrapperRefProps } from '~interfaces/refProps';
import i18n from '~lib/i18n';
import { Dimens } from '~themes';

interface FormDrawerProps extends Omit<DrawerProps, 'children'> {
	loading?: boolean;
	confirmButtonText?: string;
	abortButtonText?: string;
	children: ReactNode;
	buttonVariant?: StatusVariant;
}

const FormDrawer = ({
	confirmButtonText: confirmText = i18n.t('confirm', { ns: 'actions' }),
	abortButtonText: abortText = i18n.t('cancel', { ns: 'actions' }),
	loading,
	buttonVariant = StatusVariant.Brand,
	title,
	children,
	...drawerProps
}: FormDrawerProps) => {
	const [disabled, setDisabled] = useState(true);
	const childRef = useRef<FormWrapperRefProps>(null);

	const handleSubmit = () => {
		if (!childRef.current?.onSubmit) {
			console.warn('FormDialog expect a onSubmit handler, did you forget it?');
			return;
		}

		childRef.current.onSubmit();
	};

	const handleCancel = () => {
		if (!childRef.current?.onCancel) {
			console.warn('FormDialog expect a onCancel handler, did you forget it?');
			return;
		}

		childRef.current.onCancel();
	};

	return (
		<Drawer {...drawerProps} sx={{ ...drawerProps.sx, width: { xs: 1, sm: 'inherit' } }}>
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					height: '100%',
					width: '320px',
					paddingX: Dimens.spacingXl,
					paddingTop: Dimens.spacing5xl,
					paddingBottom: Dimens.spacing3xl,
				}}
			>
				<Stack direction='row' spacing={1} sx={{ alignItems: 'center' }}>
					{/* Isn't included in the design, but we might want something like it */}
					{/* <IconButton onClick={() => drawerProps.onClose?.({}, 'escapeKeyDown')}>
						<CloseIcon />
					</IconButton> */}
					<Typography
						sx={{
							flexGrow: 1,
						}}
					>
						{title}
					</Typography>
				</Stack>

				<Box
					sx={{
						flexGrow: 1,
						flexDirection: 'column',
						overflow: 'auto',
					}}
				>
					<FormContainerContext
						value={{
							disabled: disabled,
							setDisabled: setDisabled,
							formRef: childRef,
						}}
					>
						{children}
					</FormContainerContext>
				</Box>

				<Stack
					direction={'row'}
					spacing={1}
					sx={{
						width: '100%',
						mt: Dimens.spacing3xl,
					}}
				>
					<ActionButton
						label={abortText}
						onClick={handleCancel}
						sx={{ width: '100%' }}
						style={ActionButtonStyle.Bordered}
					/>
					<ActionButton
						loading={loading}
						variant={buttonVariant}
						disabled={disabled || loading}
						onClick={handleSubmit}
						label={confirmText}
						sx={{ width: '100%' }}
					/>
				</Stack>
			</Box>
		</Drawer>
	);
};

export default FormDrawer;
